import { CircularProgress } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useState } from "react";
import { Alert, Modal, Row } from "react-bootstrap";
import { Button } from "../common/Button";
import { useFormik } from "formik";
import * as Yup from 'yup';
import { removeMFAAuthAppSettings, requestSMSMFACode } from "../api/MFA";
import { failed, success } from "../common/Toastify";
import TextInput from "../common/textfield/TextInput";
import Lock from '../../assets/images/Lock.png';
import { useSelector } from "react-redux";
import { conditionalValidation } from "../../utils/constants";

const MFARemoveModal = ({ modalShow, handleClose, challengeType, onSuccess }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [smsSent, setSmsSent] = useState(false);
    const [smsSession, setSmsSession] = useState(null);
    const username = useSelector((state) => state?.auth?.user?.email);
    const isSMS = challengeType === "SMS_MFA";

    const formik = useFormik({
        initialValues: {
            password: "",
            mfaCode: "",
        },
        validationSchema: Yup.object({
            password: conditionalValidation(
                Yup.string().matches(/^$|^\S+.*/, 'Only blankspaces is not valid.'),
                {
                    label: "Password",
                    mandatory: true
                }
            ),
            mfaCode: conditionalValidation(
                Yup.string()
                    .matches(/^\d{6}$/, 'MFA code must be exactly 6 digits'),
                {
                    label: "MFA Code",
                    mandatory: isSMS ? smsSent : true
                }
            ),
        }),
        onSubmit: (values) => {
            if (isSMS && !smsSent) {
                handleSendSMSCode(values.password?.trim());
                return;
            }

            setIsLoading(true);
            removeMFAAuthAppSettings({
                password: values.password?.trim(),
                mfaCode: values.mfaCode?.trim(),
                challengeType: challengeType,
                session: smsSession,
            })
                .then((res) => {
                    if (res?.["$metadata"]?.httpStatusCode === 200) {
                        success("MFA Disabled Successfully");
                        onSuccess?.();
                        handleClose();
                    }
                })
                .catch((res) => {
                    failed(res?.response?.data?.message || res?.response?.data?.error || "Failed to disable MFA");
                })
                .finally(() => setIsLoading(false));
        },
    });

    const handleSendSMSCode = (password) => {
        setIsLoading(true);
        requestSMSMFACode({ username: username?.toLowerCase(), password })
            .then((res) => {
                if (res?.session) {
                    setSmsSession(res.session);
                    setSmsSent(true);
                    success("Verification code sent to your phone");
                } else {
                    failed(res?.message || "Failed to send verification code");
                }
            })
            .catch((res) => {
                failed(res?.response?.data?.message || res?.response?.data?.error || "Failed to send verification code. Please check your password.");
            })
            .finally(() => setIsLoading(false));
    };

    const handleResendCode = () => {
        setSmsSent(false);
        setSmsSession(null);
        formik.setFieldValue("mfaCode", "");
    };

    return (
        <section className="common-listing">
            <Modal
                size="lg"
                show={modalShow}
                onHide={handleClose}
                aria-labelledby="contained-modal-title-vcenter"
                backdropClassName
                backdrop={"static"}
                centered
                className="custom-dialog popup-org"
            >
                <Modal.Header closeButton className="border-0">
                    <Modal.Title id="contained-modal-title-vcenter">
                        Disable MFA
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {isSMS && !smsSent && (
                        <p>Please enter your password. A verification code will be sent to your registered phone number.</p>
                    )}
                    {isSMS && smsSent && (
                        <p>A verification code has been sent to your phone. Please enter it below to disable Multi Factor Authentication(MFA).</p>
                    )}
                    {!isSMS && (
                        <p>Please enter your password and current MFA code to disable Multi Factor Authentication(MFA).</p>
                    )}
                    <form className="custom-form" onSubmit={formik.handleSubmit}>
                        {!(isSMS && smsSent) && (
                            <Row>
                                <TextInput
                                    type="password"
                                    icon={Lock}
                                    keyField={"password"}
                                    label={"Password"}
                                    formik={formik}
                                    placeholder={"Enter your password"}
                                />
                            </Row>
                        )}
                        {(!isSMS || smsSent) && (
                            <Row>
                                <TextInput
                                    type="text"
                                    icon={Lock}
                                    keyField={"mfaCode"}
                                    label={"MFA Code"}
                                    formik={formik}
                                    placeholder={isSMS ? "Enter the code sent to your phone" : "Enter your MFA code"}
                                />
                            </Row>
                        )}
                        <div className="action-wrap">
                            {isSMS && !smsSent ? (
                                <Button type="submit" disabled={isLoading || !formik.values.password}>
                                    {isLoading ? <CircularProgress size={24} style={{ color: 'white' }} /> : 'Send Code'}
                                </Button>
                            ) : (
                                <Button type="submit" disabled={isLoading}>
                                    {isLoading ? <CircularProgress size={24} style={{ color: 'white' }} /> : 'Confirm'}
                                </Button>
                            )}
                            {isSMS && smsSent && (
                                <Button onClick={handleResendCode} variant="secondary" disabled={isLoading} style={{ marginLeft: '10px' }}>
                                    Resend Code
                                </Button>
                            )}
                            <Button onClick={handleClose} variant="secondary" title="Cancel" style={{ marginLeft: '10px' }}>
                                Cancel
                            </Button>
                        </div>
                        <Alert variant="info" className="d-flex align-items-center mt-3">
                            <InfoOutlinedIcon style={{ marginRight: "8px", color: "#055160" }} />
                            <span>If you have lost access to your MFA device, please contact IT support for assistance.</span>
                        </Alert>
                    </form>
                </Modal.Body>
            </Modal>
        </section>
    );
};

export default MFARemoveModal;
